import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  SongViewBottomBar,
  SongViewBottomButton,
  SongViewBottomSpacer,
  SongViewBottomStack,
  SongViewImage,
  SongViewMessage,
  SongViewPageInfo,
  SongViewScreen,
  SongViewStage,
} from '../components/styled';
import { useLibraryApi } from '../context/BandContext';
import { api } from '../lib/api';

function SongPageViewPage() {
  const { songId = '', bandId = '', userId = '' } = useParams();
  const defaultLibraryApi = useLibraryApi();
  const userLibraryApi = useMemo(() => userId ? api.forUser(userId) : null, [userId]);
  const libraryApi = userLibraryApi || defaultLibraryApi;
  const pathPrefix = bandId ? `/bands/${bandId}` : userId ? `/users/${userId}` : '';
  const [searchParams, setSearchParams] = useSearchParams();
  const [song, setSong] = useState(null);
  const [albumSongs, setAlbumSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const albumId = searchParams.get('albumId') || '';
  const sourceQuery = searchParams.get('q') || '';
  const sourceAlphaRange = searchParams.get('alpha') || '';
  const sourceSortBy = searchParams.get('sort') || '';

  useEffect(() => {
    let isActive = true;

    const loadSong = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const requests = [libraryApi.getSong(songId)];

        if (albumId) {
          requests.push(libraryApi.getAlbums());
        }

        const [songResponse, albumsResponse] = await Promise.all(requests);

        if (!isActive) {
          return;
        }

        setSong(songResponse?.song || null);
        setAlbumSongs(
          (albumsResponse?.albums || []).find((album) => album._id === albumId)
            ?.songs || []
        );
      } catch (error) {
        if (!isActive) {
          return;
        }

        setSong(null);
        setAlbumSongs([]);

        if (error.status !== 401) {
          setErrorMessage(error.message || 'Failed to load song.');
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadSong();

    return () => {
      isActive = false;
    };
  }, [albumId, songId, libraryApi]);

  const pageCount = song?.pages?.length || 0;
  const requestedPage = Number.parseInt(searchParams.get('page') || '1', 10);
  const normalizedRequestedPage =
    Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const currentPageNumber =
    pageCount > 0 ? Math.min(normalizedRequestedPage, pageCount) : 1;
  const currentPageIndex = currentPageNumber - 1;
  const currentPageImage = song?.pages?.[currentPageIndex] || '';

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const nextPageValue = pageCount > 1 ? String(currentPageNumber) : null;
    const currentPageValue = searchParams.get('page');

    if ((currentPageValue || null) === nextPageValue) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextPageValue) {
      nextSearchParams.set('page', nextPageValue);
    } else {
      nextSearchParams.delete('page');
    }

    setSearchParams(nextSearchParams, { replace: true });
  }, [
    currentPageNumber,
    isLoading,
    pageCount,
    searchParams,
    setSearchParams,
  ]);

  const title = useMemo(() => {
    if (isLoading) {
      return 'Loading song...';
    }

    if (song?.title) {
      return song.title;
    }

    if (errorMessage) {
      return 'Song unavailable';
    }

    return 'Untitled song';
  }, [errorMessage, isLoading, song?.title]);

  const hasPreviousPage = currentPageIndex > 0;
  const hasNextPage = currentPageIndex < pageCount - 1;
  const currentSongIndex = albumSongs.findIndex(
    (currentSong) => currentSong._id === songId
  );
  const previousSong = currentSongIndex > 0 ? albumSongs[currentSongIndex - 1] : null;
  const nextSong =
    currentSongIndex >= 0 && currentSongIndex < albumSongs.length - 1
      ? albumSongs[currentSongIndex + 1]
      : null;
  const songSearchParams = new URLSearchParams();

  if (albumId) {
    songSearchParams.set('albumId', albumId);
  }

  if (sourceQuery) {
    songSearchParams.set('q', sourceQuery);
  }

  if (sourceAlphaRange) {
    songSearchParams.set('alpha', sourceAlphaRange);
  }

  if (sourceSortBy) {
    songSearchParams.set('sort', sourceSortBy);
  }

  const songSearch = songSearchParams.toString()
    ? `?${songSearchParams.toString()}`
    : '';
  const previousSongRoute = previousSong
    ? `${pathPrefix}/song/${previousSong._id}/view${songSearch}`
    : '';
  const nextSongRoute = nextSong ? `${pathPrefix}/song/${nextSong._id}/view${songSearch}` : '';
  const hasAlbumSongNavigation =
    currentSongIndex >= 0 && albumSongs.length > 1;
  const canGoToPreviousSong = Boolean(previousSong) && currentPageNumber === 1;
  const canGoToNextSong = Boolean(nextSong) && currentPageNumber === pageCount;
  const pageInfoText =
    !isLoading && !errorMessage && pageCount > 0
      ? `Page ${currentPageNumber}/${pageCount}`
      : '';
  const songInfoText =
    !isLoading && !errorMessage && pageCount > 0 && hasAlbumSongNavigation
      ? `Song ${currentSongIndex + 1}/${albumSongs.length}`
      : '';

  const handleGoToPage = (pageNumber) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (pageNumber <= 1) {
      nextSearchParams.delete('page');
    } else {
      nextSearchParams.set('page', String(pageNumber));
    }

    setSearchParams(nextSearchParams);
  };

  return (
    <SongViewScreen>
      <SongViewStage>
        {isLoading ? (
          <SongViewMessage>Loading page images...</SongViewMessage>
        ) : null}

        {!isLoading && errorMessage ? (
          <SongViewMessage>{errorMessage}</SongViewMessage>
        ) : null}

        {!isLoading && !errorMessage && !pageCount ? (
          <SongViewMessage>No page images have been added to this song yet.</SongViewMessage>
        ) : null}

        {!isLoading && !errorMessage && currentPageImage ? (
          <SongViewImage
            src={currentPageImage}
            alt={`${song?.title || 'Song'} page ${currentPageNumber}`}
          />
        ) : null}
      </SongViewStage>

      <SongViewBottomStack>
        <SongViewPageInfo $title>
          {[title, pageInfoText, songInfoText].filter(Boolean).join(' | ')}
        </SongViewPageInfo>

        <SongViewBottomBar>
          <SongViewBottomButton as={Link} to={`${pathPrefix}/song/${songId}${songSearch}`}>
            Back
          </SongViewBottomButton>
          <SongViewBottomButton
            type="button"
            onClick={() => handleGoToPage(currentPageNumber - 1)}
            disabled={!hasPreviousPage}
          >
            Last Page
          </SongViewBottomButton>
          <SongViewBottomButton
            type="button"
            onClick={() => handleGoToPage(currentPageNumber + 1)}
            disabled={!hasNextPage}
          >
            Next Page
          </SongViewBottomButton>
          {canGoToPreviousSong ? (
            <SongViewBottomButton as={Link} to={previousSongRoute}>
              Last Song
            </SongViewBottomButton>
          ) : (
            <SongViewBottomSpacer />
          )}
          {canGoToNextSong ? (
            <SongViewBottomButton as={Link} to={nextSongRoute}>
              Next Song
            </SongViewBottomButton>
          ) : (
            <SongViewBottomSpacer />
          )}
        </SongViewBottomBar>
      </SongViewBottomStack>
    </SongViewScreen>
  );
}

export default SongPageViewPage;
